import { Box } from '@mui/material';
import { notUndefiend, valueAsString } from 'helpers/conversions';
import React from 'react';
import { StatsData } from '../Card';
import { productionIcon } from '../Icons';
import { Spinner } from '../Styles';
import { TimeFrame, TimeFrameEnum } from 'models/ReportData';
import { CustomTimeFrameProperties } from 'actions/reportFilterActions';
import {
  OverviewTopCardNames,
  getDescriptionTextForCustomTimeframe,
  getTopCardArrowColor,
} from 'helpers/overviewUtils';
import { globalColors, NoDataIconWrapper } from 'globalStyle';
import EmptyStateIcon, { NO_DATA_FOR_PERIOD_MESSAGE } from 'components/_common/EmptyState';
import { styles } from './DesktopTotalImpressionCard.styles';
import { ReactComponent as ArrowUpIcon } from '../../../assets/icons/TopCardsArrowUp.svg';
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/TopCardsArrowDown.svg';

type ChartsHeadersForCustomTimeframeType = {
  timeframe: TimeFrame;
  customTimeframeProperties: CustomTimeFrameProperties;
};
const DesktopTotalImpressionCard = props => {
  const {
    stats,
    description,
    value,
    selectedSystemGroup,
    units,
    selectedSystemGroupText,
    timeframe,
    customTimeframeProperties,
    addedData,
    //*Production Hours mode (DTG/Apollo only): `value` is the impressions within production hours (backend `totalProduction`)
    //*and `secondaryMetric` is the general total (backend `totalProductionFull`) - shows a dash if the backend omits it.
    secondaryMetric,
  } = props;
  const isNegative = React.useMemo(() => {
    if (!notUndefiend([stats])) {
      return false;
    }
    if (stats! < 0) {
      return true;
    }
    if (description === 'Average Availability' && value !== undefined && value >= 0.999) {
      return false;
    }
    return false;
  }, [description, stats, value]);

  const icons = productionIcon(selectedSystemGroup, 30, 32);
  const getTimeframeTextKey = () => {
    return timeframe && timeframe.includes('lastUpdate') ? 'last' : 'weekDay';
  };
  const statsDescription =
    selectedSystemGroupText.impression.statsDescription[getTimeframeTextKey()];
  const hasAddedData = !!addedData;
  const getArrowIcon = (data, row) => {
    if (!data[row]) {
      return <></>;
    }
    const trend = data[row].trend;
    const description = data[row].description;
    if (trend === 0 || trend === undefined) {
      return <></>;
    }
    const arrowColor =
      description === 'Dark Impressions'
        ? globalColors.regular.arrows.gray
        : getTopCardArrowColor({
            cardName: OverviewTopCardNames.averageProductionRate,
            trend,
            description,
          });
    return trend > 0 ? <ArrowUpIcon fill={arrowColor} /> : <ArrowDownIcon fill={arrowColor} />;
  };

  if (value === null) {
    return (
      <NoDataIconWrapper>
        <EmptyStateIcon icon="no-icon" message={NO_DATA_FOR_PERIOD_MESSAGE} />
      </NoDataIconWrapper>
    );
  }

  return notUndefiend([value]) ? (
    <Box
      display="flex"
      justifyContent="space-between"
      width="100%"
      height="100%"
      boxSizing="border-box"
      sx={styles.innerCardContent}
      data-testid="total-production-card">
      <Box
        display="flex"
        boxSizing="border-box"
        height="100%"
        sx={
          secondaryMetric
            ? { flex: '1 1 0', minWidth: 0 }
            : { width: hasAddedData ? '57%' : '100%' }
        }>
        <Box
          display="flex"
          flexDirection="column"
          boxSizing="border-box"
          width="100%"
          height="100%">
          <Box
            display="flex"
            width="100%"
            height="80px"
            boxSizing="border-box"
            sx={styles.upperRow}>
            <Box display="flex" flexDirection="column" sx={{ flex: '1 1 0', minWidth: 0 }}>
              <Box display="flex" alignItems="flex-start">
                {isNegative ? icons.negative : icons.positive}
                <Box sx={styles.value} data-testid="total-impression-value">
                  {value !== undefined ? valueAsString(value, false) : '—'}
                </Box>
                <Box sx={styles.units} data-testid="total-impression-units">
                  {value !== undefined ? units : ''}
                </Box>
              </Box>
              <Box sx={styles.description} data-testid="total-impression-description">
                {description}
              </Box>
            </Box>
            {secondaryMetric && (
              <>
                <Box sx={styles.secondaryDivider} />
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{ flex: '1 1 0', minWidth: 0 }}
                  data-testid="secondary-metric-column">
                  <Box display="flex" alignItems="flex-start" sx={styles.secondaryValueRow}>
                    <Box sx={styles.value} data-testid="secondary-metric-value">
                      {secondaryMetric.value !== undefined
                        ? valueAsString(secondaryMetric.value, false)
                        : '—'}
                    </Box>
                    <Box sx={styles.units} data-testid="secondary-metric-units">
                      {secondaryMetric.value !== undefined ? secondaryMetric.unit : ''}
                    </Box>
                  </Box>
                  <Box sx={styles.description} data-testid="secondary-metric-description">
                    {secondaryMetric.description}
                  </Box>
                </Box>
              </>
            )}
          </Box>
          <Box
            display="flex"
            alignItems="flex-end"
            width="100%"
            height="100%"
            boxSizing="border-box"
            sx={styles.bottomRow}>
            {notUndefiend([stats]) && StatsData(stats!)}{' '}
            <div>
              {timeframe === TimeFrameEnum.custom
                ? getDescriptionTextForCustomTimeframe({ timeframe, customTimeframeProperties })
                : statsDescription}
            </div>
          </Box>
        </Box>
      </Box>{' '}
      {hasAddedData && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          boxSizing="border-box"
          height="100%"
          sx={
            //*in Production Hours mode the two metrics on the left need the room, so the grey block gets a fixed width instead of a share
            secondaryMetric
              ? { ...styles.rightColumn, flex: '0 0 140px' }
              : { ...styles.rightColumn, width: '40%' }
          }
          data-testid="right-column">
          {addedData?.upper && (
            <Box
              display="flex"
              alignItems="center"
              boxSizing="border-box"
              width="100%"
              height="50px"
              sx={styles.rightColumnUpperRow}>
              <Box
                display="flex"
                width="20px"
                alignSelf="center"
                sx={styles.rightColumnArrowPlaceHolder}>
                {getArrowIcon(addedData, 'upper')}
              </Box>
              <Box display="flex" flexDirection="column" sx={styles.rightColumnContent}>
                <Box display="flex" sx={styles.rightColumnContentUpperRow}>
                  {addedData?.upper?.formatter
                    ? addedData.upper.formatter(addedData.upper.value)
                    : valueAsString(addedData?.upper?.value)}{' '}
                  {addedData?.upper?.unit}
                </Box>
                <Box sx={styles.rightColumnContentBottomRow}>{addedData?.upper?.description}</Box>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  ) : (
    Spinner(60)
  );
};
export default DesktopTotalImpressionCard;
